import { ReactNode } from "react";
import { Box, styled, Theme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { tokens } from "@styles/tokens";

interface FieldCardProps {
  /** Uppercase label shown above the field row. */
  label: string;
  /** Leading icon (or status dot) inside the row. */
  icon?: ReactNode;
  /** Makes the row a tappable button (opens a picker) with a trailing chevron. */
  onClick?: () => void;
  /** Replaces the default trailing chevron (e.g. a "units" suffix). */
  trailing?: ReactNode;
  /** Vertical alignment of the row content — "start" for multiline fields. */
  align?: "center" | "start";
  helper?: string;
  error?: string;
  disabled?: boolean;
  children: ReactNode;
}

/**
 * A labelled field "card" — the building block of the redesigned forms: an
 * uppercase label over a rounded surface row with an optional leading icon,
 * content, and a trailing affordance (chevron when it opens a picker, or a
 * custom suffix). Helper text sits below; an error replaces it and reddens the
 * border. Renders as a button when `onClick` is set, else a static container.
 */
export const FieldCard = ({
  label,
  icon,
  onClick,
  trailing,
  align = "center",
  helper,
  error,
  disabled,
  children,
}: FieldCardProps) => {
  const inner = (
    <>
      {icon && <Leading>{icon}</Leading>}
      <Content>{children}</Content>
      {trailing ?? (onClick && <ChevronRight size={18} />)}
    </>
  );

  return (
    <Wrapper>
      <Label>{label}</Label>
      {onClick ? (
        <RowButton
          type="button"
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          $interactive
          $error={!!error}
          $align={align}
          aria-label={label}
        >
          {inner}
        </RowButton>
      ) : (
        <RowStatic $interactive={false} $error={!!error} $align={align}>
          {inner}
        </RowStatic>
      )}
      {(error || helper) && <Sub $error={!!error}>{error || helper}</Sub>}
    </Wrapper>
  );
};

type RowProps = { $interactive: boolean; $error: boolean; $align: "center" | "start" };

const rowStyle = ({
  theme,
  $interactive,
  $error,
  $align,
}: RowProps & { theme: Theme }) => ({
  display: "flex",
  alignItems: $align === "start" ? "flex-start" : "center",
  gap: tokens.space(2.5),
  width: "100%",
  minHeight: 52,
  padding: `${$align === "start" ? tokens.space(2) : 0}px ${tokens.space(3)}px`,
  borderRadius: tokens.radius.md,
  textAlign: "left" as const,
  backgroundColor: theme.skuey.surface,
  border: `1px solid ${$error ? theme.skuey.danger : theme.skuey.border}`,
  color: theme.skuey.ink,
  cursor: $interactive ? "pointer" : "default",
  transition: "border-color .12s ease",
  ...($interactive && {
    "&:hover": { borderColor: theme.skuey.borderStrong },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  }),
});

const filterRowProps = (p: string) =>
  p !== "$interactive" && p !== "$error" && p !== "$align";

const RowButton = styled("button", { shouldForwardProp: filterRowProps })<RowProps>(
  rowStyle
);

const RowStatic = styled("div", { shouldForwardProp: filterRowProps })<RowProps>(
  rowStyle
);

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(1.5),
});

const Label = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  fontWeight: tokens.font.weight.semibold,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.skuey.muted,
}));

const Leading = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.skuey.subtle,
}));

const Content = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: tokens.space(2),
});

const Sub = styled("span", {
  shouldForwardProp: (p) => p !== "$error",
})<{ $error: boolean }>(({ theme, $error }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: $error ? theme.skuey.danger : theme.skuey.subtle,
}));
